import React from 'react'
import { Trans } from 'react-i18next'

import Caption from 'components/Luxkit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'

interface Props {
  className?: string;
  align?: 'start' | 'center' | 'end';
  price: number;
  forTwoAdults: boolean;
  priceColour?: React.ComponentProps<typeof Caption>['colour'];
  'data-testid'?: string;
  pricePerPackage?: boolean;
}

function HotelPlusFlightsFrom({ className, align, price, forTwoAdults, priceColour = 'neutral-two', 'data-testid': testId, pricePerPackage = false }: Props) {
  /*
    Be careful about changing the structure of what's inside the Trans component,
    because it has to match the placeholders that are in the corresponding
    translation strings.

    That's why there are two separate JSX blocks for `forTwoAdults` and `!forTwoAdults`,
    even though they're mostly the same. We can't just toggle the last Caption block
    on or off, because then the JSX structure wouldn't match the translation string.

    That's the mean reason this is in its own component - so we can manage that
    complexity in one place, with this comment to explain it.
  */

  const translationKeyForTwoAdults = pricePerPackage ? 'hotelFlightsForTwoAdultsPerPackagePricing' : 'hotelsFlightsForTwoAdults'
  const translationKeyNoGuestCount = pricePerPackage ? 'hotelFlightsPriceNoGuestCountPerPackagePricing' : 'hotelsFlightsPriceNoGuestCount'

  const priceSuffix = pricePerPackage ? '/package*' : '*'

  return (
    <BodyTextBlock
      className={className}
      variant="medium"
      wrap="pre-wrap"
      align={align}
      colour="neutral-two"
      startIcon={<LinePlaneFlyIcon colour="neutral-one" />}
      data-testid={testId}
    >
      {forTwoAdults && <Trans i18nKey={translationKeyForTwoAdults} ns="hotelOfferList">
        <Caption variant="medium" as="span">Hotel + flights from&nbsp;</Caption>
        <Caption variant="medium" as="span" colour={priceColour} weight="bold">
          <FormatCurrency value={price} format="roundedDollar" />{priceSuffix}&nbsp;
        </Caption>
        <Caption variant="medium" as="span" colour="neutral-three">
          (Two Adults)
        </Caption>
      </Trans>}
      {!forTwoAdults && <Trans i18nKey={translationKeyNoGuestCount} ns="hotelOfferList">
        <Caption variant="medium" as="span">Hotel + flights from&nbsp;</Caption>
        <Caption variant="medium" as="span" colour={priceColour} weight="bold">
          <FormatCurrency value={price} format="roundedDollar" />{priceSuffix}&nbsp;
        </Caption>
      </Trans>}
    </BodyTextBlock>
  )
}

export default HotelPlusFlightsFrom
